import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { store } from "../redux/store";

// Registro de lo que pasa en la web, enviado al backend de gestionVentas.
//
// REGLA: la web NUNCA depende de esto. Nada de acá bloquea, demora ni rompe el
// login o una venta:
// - todo es fire-and-forget y está envuelto en try/catch;
// - usa su propio cliente axios (NO apiClient: un 401 del backend jamás debe
//   cerrar la sesión del promotor);
// - si el backend no responde, el registro queda en una cola en el navegador y
//   se reenvía después (cada minuto, al recuperar conexión y al volver a entrar).
//
// La cola guarda datos del cliente en el navegador hasta que se envían (como
// mucho 7 días). El token ya está en el navegador por redux-persist.

// En producción el backend está en el mismo dominio (/api, vía Apache).
// En desarrollo queda apagado salvo que se configure REACT_APP_REGISTRO_URL.
const URL_REGISTRO =
  process.env.REACT_APP_REGISTRO_URL ??
  (process.env.NODE_ENV === "production" ? "/api" : "");

const http = axios.create({ baseURL: URL_REGISTRO, timeout: 5000 });

const CLAVE_COLA = "gv-registro-pendientes";
const MAX_ITEMS = 300;
const MAX_EDAD_MS = 7 * 24 * 60 * 60 * 1000;
const MAX_EVENTOS_POR_LOTE = 50;
const CLAIM_NOMBRE = "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name";

export const registroHabilitado = () => Boolean(URL_REGISTRO);

export function nuevoId() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  // Navegadores viejos / contexto no seguro: UUID v4 con getRandomValues.
  const b = window.crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = [...b].map((x) => x.toString(16).padStart(2, "0")).join("");
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

// --- Credenciales --------------------------------------------------------------

function base64url(texto) {
  const bytes = new TextEncoder().encode(texto);
  let bin = "";
  bytes.forEach((b) => {
    bin += String.fromCharCode(b);
  });
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function leerToken(token) {
  try {
    const c = jwtDecode(token);
    return {
      usuario: String(c[CLAIM_NOMBRE] || "").toLowerCase(),
      exp: (c.exp || 0) * 1000,
    };
  } catch {
    return null;
  }
}

// Token + datos del login de la sesión actual, o null.
function credencialesActuales() {
  const u = store.getState().user.currentUser;
  const token = u?.data?.token || u?.token;
  const md = u?.metadata || u?.data?.metadata;
  if (!token || !md?.usuarioId) return null;
  const info = leerToken(token);
  if (!info) return null;
  return {
    token,
    usuario: info.usuario,
    exp: info.exp,
    meta: {
      usuarioId: md.usuarioId,
      promotorId: md.promotorId ?? null,
      promotorNombre: md.promotorNombre ?? null,
    },
  };
}

const vigente = (cred) => cred && cred.exp > Date.now() + 5000;

// --- Cola ----------------------------------------------------------------------

function leerCola() {
  try {
    const cola = JSON.parse(localStorage.getItem(CLAVE_COLA) || "[]");
    return Array.isArray(cola) ? cola : [];
  } catch {
    return [];
  }
}

function guardarCola(cola) {
  try {
    const limite = Date.now() - MAX_EDAD_MS;
    let vigentes = cola.filter((i) => i.creado > limite);
    if (vigentes.length > MAX_ITEMS) {
      // Si hay que recortar, se sacan primero los eventos más viejos: las ventas
      // y las solicitudes (su única copia vive en el backend) son lo último que
      // se pierde.
      const esImportante = (i) => i.tipo === "venta" || i.tipo === "solicitud";
      const importantes = vigentes.filter(esImportante);
      const otros = vigentes.filter((i) => !esImportante(i));
      vigentes = [...otros.slice(Math.max(0, otros.length - (MAX_ITEMS - importantes.length))), ...importantes];
    }
    localStorage.setItem(CLAVE_COLA, JSON.stringify(vigentes));
  } catch {
    // localStorage lleno o bloqueado: el registro se pierde, la web sigue.
  }
}

function encolar(tipo, datos, clave) {
  const cred = credencialesActuales();
  if (!cred) return;
  const cola = leerCola();
  const item = { id: nuevoId(), tipo, creado: Date.now(), clave, datos, cred };
  // Para una venta alcanza con su último estado: reemplaza al anterior.
  const i = clave ? cola.findIndex((x) => x.clave === clave) : -1;
  if (i >= 0) cola[i] = item;
  else cola.push(item);
  guardarCola(cola);
}

// --- Envío ---------------------------------------------------------------------

function headers(cred) {
  return {
    Authorization: `Bearer ${cred.token}`,
    "X-Iptel-Meta": base64url(JSON.stringify(cred.meta)),
  };
}

// Respuesta real del backend (JSON). Si /api todavía no está ruteado al
// backend, Apache devuelve el index.html de la web con 200: eso NO es un
// registro aceptado.
const esRespuestaDelBackend = (res) =>
  Boolean(res?.data) && typeof res.data === "object" && !Array.isArray(res.data);

// "ok" | "reintentar" | "descartar" | "otra-credencial"
async function enviar(item, cred) {
  try {
    const cfg = { headers: headers(cred) };
    let res;
    if (item.tipo === "login") res = await http.post("/log/login", {}, cfg);
    else if (item.tipo === "venta")
      res = await http.put(`/log/ventas/${item.clave.slice("venta:".length)}`, item.datos, cfg);
    else if (item.tipo === "solicitud")
      res = await http.put(`/log/solicitudes/${item.clave.slice("solicitud:".length)}`, item.datos, cfg);
    else res = await http.post("/log/eventos", { eventos: item.datos }, cfg);
    return esRespuestaDelBackend(res) ? "ok" : "reintentar";
  } catch (e) {
    const st = e?.response?.status;
    if (!st || st === 429 || st >= 500) return "reintentar";
    if (st === 401 && !vigente(cred)) return "otra-credencial";
    // 400 / 403 / 404: el backend nunca lo va a aceptar.
    console.warn("[registro] descartado", item.tipo, st, e?.response?.data);
    return "descartar";
  }
}

// Credencial para enviar un ítem: la suya si sigue vigente; si venció, la
// sesión actual siempre que sea del mismo usuario.
function credencialPara(item) {
  if (vigente(item.cred)) return item.cred;
  const actual = credencialesActuales();
  if (vigente(actual) && actual.usuario === item.cred.usuario) return actual;
  return null;
}

// Un solo vaciado a la vez. Si se pide otro mientras corre, se marca y el
// vaciado en curso da otra vuelta; quien llama recibe la misma promesa, que
// termina recién cuando no quedó nada pendiente.
let vaciadoEnCurso = null;
let pendienteOtraVez = false;

export function vaciarCola() {
  if (!registroHabilitado()) return Promise.resolve();
  if (vaciadoEnCurso) {
    pendienteOtraVez = true;
    return vaciadoEnCurso;
  }
  vaciadoEnCurso = (async () => {
    try {
      do {
        pendienteOtraVez = false;
        await vaciarUnaVez();
      } while (pendienteOtraVez);
    } catch (e) {
      console.warn("[registro] error vaciando la cola", e);
    } finally {
      vaciadoEnCurso = null;
    }
  })();
  return vaciadoEnCurso;
}

async function vaciarUnaVez() {
  const orden = { login: 0, venta: 1, solicitud: 1, evento: 2 };
  const cola = leerCola().sort((a, b) => orden[a.tipo] - orden[b.tipo] || a.creado - b.creado);
  const resueltos = new Set();

  // Los eventos se mandan en lotes por credencial.
  const lotes = new Map();
  for (const item of cola) {
    const cred = credencialPara(item);
    if (!cred) continue; // sin credencial usable: espera una sesión del mismo usuario
    if (item.tipo === "evento") {
      const k = cred.token;
      if (!lotes.has(k)) lotes.set(k, { cred, items: [] });
      lotes.get(k).items.push(item);
      continue;
    }
    const r = await enviar(item, cred);
    if (r === "reintentar") return marcar(resueltos); // backend caído: no insistir ahora
    if (r !== "otra-credencial") resueltos.add(item.id);
  }

  for (const { cred, items } of lotes.values()) {
    for (let i = 0; i < items.length; i += MAX_EVENTOS_POR_LOTE) {
      const parte = items.slice(i, i + MAX_EVENTOS_POR_LOTE);
      const r = await enviar({ tipo: "eventos", datos: parte.map((x) => x.datos) }, cred);
      if (r === "reintentar") return marcar(resueltos);
      if (r !== "otra-credencial") parte.forEach((x) => resueltos.add(x.id));
    }
  }
  marcar(resueltos);
}

// Saca de la cola lo ya resuelto, releyéndola (pudieron entrar ítems nuevos).
function marcar(resueltos) {
  if (resueltos.size === 0) return;
  guardarCola(leerCola().filter((i) => !resueltos.has(i.id)));
}

// --- API pública -----------------------------------------------------------------

function seguro(fn) {
  try {
    if (!registroHabilitado()) return;
    fn();
    void vaciarCola();
  } catch (e) {
    console.warn("[registro]", e);
  }
}

// Después de un login exitoso contra Iptel.
export function registrarLogin() {
  seguro(() => encolar("login", {}, `login:${nuevoId()}`));
}

// Estado de una venta. Llamar con el MISMO origenId para el intento y el resultado.
// body: { fechaVenta, estado, payload, resumen?, meta?, resultado? }
export function registrarVenta(origenId, body) {
  seguro(() => encolar("venta", body, `venta:${origenId}`));
}

// Solicitud de servicio (prospecto en zona sin servicio). Respaldo de la cola:
// el formulario intenta primero mandarla directo con `pedidoBackend` para poder
// confirmarla; solo si el backend no responde la encola acá y se reintenta.
// Mismo origenId → el backend no la duplica.
// body: { fechaSolicitud, nombre, apellido, telefono, provincia, localidad, ... }
export function registrarSolicitud(origenId, body) {
  seguro(() => encolar("solicitud", body, `solicitud:${origenId}`));
}

// tipo: web.logout | web.sesion-vencida | cliente.consulta | formulario.salida |
//       formulario.limpiar | plan.limpiar | venta.snapshot | web.error
export function registrarEvento(tipo, mensaje, detalle) {
  seguro(() =>
    encolar("evento", {
      origenId: nuevoId(),
      tipo,
      fecha: new Date().toISOString(),
      ...(mensaje ? { mensaje: String(mensaje).slice(0, 300) } : {}),
      ...(detalle !== undefined ? { detalle } : {}),
    }),
  );
}

// Login fallido: no hay token todavía, así que va por una ruta sin identidad.
// Es lo que permite ver después que alguien no acierta la contraseña.
export function registrarLoginFallido(usuario, motivo, httpStatus) {
  try {
    if (!registroHabilitado()) return;
    http
      .post("/log/login-fallido", {
        origenId: nuevoId(),
        usuario: String(usuario || "").slice(0, 100),
        fecha: new Date().toISOString(),
        ...(motivo ? { motivo: String(motivo).slice(0, 300) } : {}),
        ...(httpStatus ? { httpStatus } : {}),
      })
      .catch(() => {});
  } catch (e) {
    console.warn("[registro]", e);
  }
}

// Pedido autenticado al backend (panel). A diferencia del registro, acá el
// llamador SÍ espera la respuesta y maneja el error: el panel depende del
// backend (la web de ventas no).
export function pedidoBackend(metodo, ruta, { params, datos, responseType } = {}) {
  const cred = credencialesActuales();
  if (!registroHabilitado()) return Promise.reject(new Error("El panel no está disponible en este entorno"));
  if (!vigente(cred)) return Promise.reject(new Error("Sesión vencida. Volvé a iniciar sesión."));
  return http.request({
    method: metodo,
    url: ruta,
    params,
    data: datos,
    responseType,
    headers: headers(cred),
    timeout: 60000,
  });
}

// Consulta directa (sin cola) para saber si mostrar el panel. Nunca lanza.
export async function consultarAcceso() {
  try {
    const cred = credencialesActuales();
    if (!registroHabilitado() || !vigente(cred)) return null;
    const res = await http.get("/log/me", { headers: headers(cred) });
    return esRespuestaDelBackend(res) ? res.data : null;
  } catch {
    return null;
  }
}

// Arranque: vaciado periódico y captura de errores no manejados.
let iniciado = false;
export function iniciarRegistro() {
  if (iniciado || !registroHabilitado()) return;
  iniciado = true;
  try {
    void vaciarCola();
    setInterval(() => void vaciarCola(), 60 * 1000);
    window.addEventListener("online", () => void vaciarCola());

    let errores = 0;
    const informar = (mensaje, detalle) => {
      if (errores >= 5) return; // tope por carga de página
      errores += 1;
      registrarEvento("web.error", mensaje, { ...detalle, ruta: window.location.pathname });
    };
    window.addEventListener("error", (ev) =>
      informar(ev.message || "Error", { archivo: ev.filename, linea: ev.lineno }),
    );
    window.addEventListener("unhandledrejection", (ev) =>
      informar(String(ev.reason?.message || ev.reason || "Promesa rechazada"), {}),
    );
  } catch (e) {
    console.warn("[registro] no se pudo iniciar", e);
  }
}
